/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { service } from '@ember/service';
import config from 'my-phorest/config/environment';
import { variation } from 'ember-launch-darkly';
import { action } from '@ember/object';
import { dropTask } from 'ember-concurrency';
import issueZendeskSsoTokenMutation from 'my-phorest/gql/mutations/issue-zendesk-sso-token.graphql';
import issueZendeskMessagingTokenMutation from 'my-phorest/gql/mutations/issue-zendesk-messaging-token.graphql';

export default class SupportDialogComponent extends Component {
  @service apollo;
  @service intl;
  @service notifications;
  @service reviewableTickets;
  @service router;
  @service session;

  get businessId() {
    return this.session.business.id;
  }

  get branchId() {
    return this.session.branch.id;
  }

  get hasReviewableTickets() {
    return this.reviewableTickets.reviewableTicketsCount > 0;
  }

  get embeddedPath() {
    return `businesses/${this.businessId}/branches/${this.branchId}/support-review`;
  }

  get currentUserName() {
    return this.session.user?.name || this.session.branch?.fullName;
  }

  get branchAccountId() {
    return this.session.branch.accountId;
  }

  get suggestionUrl() {
    const forums = {
      de: 'https://phorest.canny.io/deutsches-forum',
      fi: 'https://phorest.canny.io/finnish-forum',
    };

    return forums[this.intl.defaultLocale] || 'https://phorest.canny.io';
  }

  get widgetKey() {
    if (!variation('release-zendesk-sandbox-support')) {
      return config.zendesk.classicKey;
    }
    return config.zendesk.sandBoxKey;
  }

  get zendeskWidgetLoaded() {
    return typeof window.zE === 'function';
  }

  @dropTask
  *issueZendeskToken() {
    return yield this.apollo.mutate(
      {
        mutation: issueZendeskSsoTokenMutation,
      },
      'issueZendeskSsoToken'
    );
  }

  @dropTask
  *issueMessagingToken() {
    return yield this.apollo.mutate(
      {
        mutation: issueZendeskMessagingTokenMutation,
      },
      'issueZendeskMessagingToken'
    );
  }

  @action
  async loadZendeskWidget() {
    if (variation('release-zendesk-messaging')) {
      if (this.zendeskWidgetLoaded) {
        await this._reAuthenticateExistingWidget();
        return;
      }

      await this._initializeMessagingWidget();
    } else {
      await this._initializeClassicWidget();
    }
  }

  async _reAuthenticateExistingWidget() {
    try {
      const token = await this._fetchMessagingToken();

      if (token) {
        window.zE('messenger', 'loginUser', function (callback) {
          callback(token);
        });
      }

      window.zE('messenger', 'show');
      window.zE('messenger', 'open');
    } catch (error) {
      console.error("Couldn't issue zendesk token", error);
    }
  }

  async _fetchMessagingToken() {
    await this.issueZendeskToken.perform();
    const result = await this.issueMessagingToken.perform();

    return result.tokens?.find(
      (token) => token.businessId === this.session.businessId
    )?.accessToken;
  }

  async _initializeMessagingWidget() {
    let messagingToken;

    try {
      messagingToken = await this._fetchMessagingToken();
    } catch (error) {
      console.error("Couldn't issue zendesk token", error);
    }

    this._configureWidgetSettings();
    this._loadWidgetScript(() => {
      this._setupMessagingWidget(messagingToken);
    });
  }

  async _initializeClassicWidget() {
    this._configureWidgetSettings();
    this._loadWidgetScript(() => {
      this._setupClassicWidget();
    }, true);
  }

  _configureWidgetSettings() {
    window.zESettings = {
      webWidget: {
        color: { theme: '#115E59' },
        contactForm: {
          suppress: false,
          attachments: false,
          ticketForms: [{ id: config.zendesk.ticketFormId }],
          fields: [
            { id: 'name', prefill: { '*': this.currentUserName } },
            {
              id: 'email',
              prefill: {
                '*': this.args.currentUserEmail,
              },
            },
            { id: 360008170354, prefill: { '*': this.branchAccountId } },
          ],
        },
        helpCenter: { suppress: false },
      },
    };
  }

  _loadWidgetScript(onLoaded, isClassic = false) {
    const script = document.createElement('script');
    script.id = 'ze-snippet';
    script.src = `https://static.zdassets.com/ekr/snippet.js?key=${this.widgetKey}`;

    let timeoutId;

    const handleError = () => {
      clearTimeout(timeoutId);

      this.notifications.failure(
        this.intl.t('app-main-nav.support-dialog.contact-us-error.title'),
        this.intl.t('app-main-nav.support-dialog.contact-us-error.description'),
        {
          sticky: true,
        }
      );
    };

    timeoutId = setTimeout(() => {
      if (typeof window.zE !== 'function') {
        handleError();
      }
    }, 5000);

    script.onload = () => {
      const intervalId = setInterval(() => {
        const hasRequiredMethod = isClassic
          ? typeof window.zE.activate === 'function'
          : true;

        if (typeof window.zE === 'function' && hasRequiredMethod) {
          clearInterval(intervalId);
          clearTimeout(timeoutId);
          onLoaded();
        }
      }, 100);
    };

    script.onerror = handleError;

    document.body.appendChild(script);
  }

  _setupMessagingWidget(messagingToken) {
    window.zE('messenger', 'open');

    window.zE('messenger:on', 'close', function () {
      window.zE('messenger', 'hide');
    });

    if (messagingToken) {
      window.zE('messenger', 'loginUser', function (callback) {
        callback(messagingToken);
      });
    }
  }

  _setupClassicWidget() {
    window.zE.activate({ hideOnClose: true });

    window.zE('webWidget:on', 'close', function () {
      window.zE('webWidget', 'reset');
    });
  }
}
